import { useState } from "react";
import { useForm } from "react-hook-form";
import axios from "axios";
import { KeyRound } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";

const API = "/api";

export default function ChangePasswordDialog() {
    const [open, setOpen] = useState(false);
    const [status, setStatus] = useState("idle");
    const [serverError, setServerError] = useState("");
    const {
        register,
        handleSubmit,
        reset,
        watch,
        formState: { errors, isSubmitting },
    } = useForm();

    const onSubmit = async (data) => {
        setServerError("");
        try {
            await axios.post(`${API}/admin/change-password`, data, { withCredentials: true });
            reset();
            setStatus("success");
        } catch (e) {
            const detail = e.response?.data?.detail;
            setServerError(typeof detail === "string" ? detail : "Could not update the password. Please try again.");
        }
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(v) => {
                setOpen(v);
                if (!v) {
                    setStatus("idle");
                    setServerError("");
                }
            }}
        >
            <DialogTrigger asChild>
                <button
                    type="button"
                    data-testid="admin-change-password-button"
                    className="inline-flex h-12 items-center gap-2 rounded-full border-2 border-ink px-5 text-base font-semibold text-ink transition-colors duration-200 hover:bg-ink hover:text-cream"
                >
                    <KeyRound className="h-4 w-4" aria-hidden="true" />
                    <span className="hidden sm:inline">Change password</span>
                </button>
            </DialogTrigger>
            <DialogContent className="max-w-md rounded-3xl border-clay bg-white" data-testid="admin-pw-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl font-extrabold text-ink">Change password</DialogTitle>
                    <DialogDescription className="text-lg leading-relaxed text-ink-soft">
                        Choose a new password of at least 10 characters. Any other signed-in sessions will stop
                        working.
                    </DialogDescription>
                </DialogHeader>
                {status === "success" ? (
                    <p role="status" data-testid="admin-pw-success" className="rounded-2xl border border-teal-600/30 bg-teal-50 p-4 text-base font-semibold text-teal-800">
                        Password updated. Use it the next time you sign in.
                    </p>
                ) : (
                    <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
                        <div>
                            <label htmlFor="pw-current" className="field-label">
                                Current password
                            </label>
                            <input
                                id="pw-current"
                                type="password"
                                data-testid="admin-pw-current-input"
                                className="input-field"
                                autoComplete="current-password"
                                {...register("current_password", { required: "Please enter your current password." })}
                            />
                            {errors.current_password && (
                                <p className="error-text" role="alert" data-testid="admin-pw-current-error">
                                    {errors.current_password.message}
                                </p>
                            )}
                        </div>
                        <div>
                            <label htmlFor="pw-new" className="field-label">
                                New password
                            </label>
                            <input
                                id="pw-new"
                                type="password"
                                data-testid="admin-pw-new-input"
                                className="input-field"
                                autoComplete="new-password"
                                {...register("new_password", {
                                    required: "Please choose a new password.",
                                    minLength: { value: 10, message: "Use at least 10 characters." },
                                })}
                            />
                            {errors.new_password && (
                                <p className="error-text" role="alert" data-testid="admin-pw-new-error">
                                    {errors.new_password.message}
                                </p>
                            )}
                        </div>
                        <div>
                            <label htmlFor="pw-confirm" className="field-label">
                                Repeat new password
                            </label>
                            <input
                                id="pw-confirm"
                                type="password"
                                data-testid="admin-pw-confirm-input"
                                className="input-field"
                                autoComplete="new-password"
                                {...register("confirm_password", {
                                    required: "Please repeat the new password.",
                                    validate: (v) => v === watch("new_password") || "The two passwords don't match.",
                                })}
                            />
                            {errors.confirm_password && (
                                <p className="error-text" role="alert" data-testid="admin-pw-confirm-error">
                                    {errors.confirm_password.message}
                                </p>
                            )}
                        </div>
                        {serverError && (
                            <p className="error-text" role="alert" data-testid="admin-pw-server-error">
                                {serverError}
                            </p>
                        )}
                        <button type="submit" disabled={isSubmitting} data-testid="admin-pw-submit" className="btn-primary w-full">
                            {isSubmitting ? "Updating…" : "Update password"}
                        </button>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}